import React, { useEffect, useState } from 'react';

import { MenuItem, Skeleton, TextField, Typography } from '@mui/material';
import { useTheme } from '@apigrowfinance/pbolt-theme-provider';
import dayjs from 'dayjs';
import { RiTimeLine } from 'react-icons/ri';
import { FormattedMessage } from 'react-intl';

import OperatorSessionExtendedPanel from '@components/operatorSession/OperatorSessionExtendedPanel';
import { useAccount } from '@contexts/AccountProvider';
import { useOperatorSession } from '@contexts/OperatorSeesionProvider';
import sessionOutcomes from '@messages/sessionOutcomes';
import getEnumTranslation from '@utils/getEnumTranslation';

type OperatorSessionReportProps = {
    isOpen: boolean;
    close: () => void;
};

const OperatorSessionReport = ({
    isOpen,
    close,
}: OperatorSessionReportProps) => {
    const { additionalInfo, addNotes, addResolution, sessionStartTime } =
        useOperatorSession();
    const { account } = useAccount();

    const { theme } = useTheme();

    const [time, setTime] = useState(dayjs());

    useEffect(() => {
        const interval = setInterval(() => setTime(dayjs()), 1000 * 60);
        return () => {
            clearInterval(interval);
        };
    }, []);

    return (
        <OperatorSessionExtendedPanel isOpen={isOpen} closePanel={close}>
            <Typography
                variant="h5"
                component="h1"
                sx={{
                    color: theme?.custom?.brand?.textMain || 'text.primary',
                }}>
                <FormattedMessage
                    id="OperatorSessionReport.title"
                    defaultMessage="{accountHolder}'s Report"
                    values={{ accountHolder: account?.name }}
                />
            </Typography>

            <Typography
                data-testid="session-time"
                variant="body2"
                color="text.secondary"
                sx={{
                    mb: theme.spacing(6.25),
                }}>
                <RiTimeLine
                    style={{
                        marginRight: '5px',
                        marginBottom: '-1px',
                    }}
                />
                {sessionStartTime ? (
                    <>
                        {sessionStartTime.format('HH:mm')}
                        {' > '}
                        {time.format('HH:mm')}
                        {' . '}
                        {/* TODO: format this better */}
                        {dayjs
                            .duration(time?.diff(sessionStartTime))
                            .format('HH:mm')}
                    </>
                ) : (
                    <Skeleton
                        width={theme.spacing(15)}
                        data-testid="session-loader"
                    />
                )}
            </Typography>

            <TextField
                data-testid="outcome-textfield"
                sx={{ mb: 0 }}
                label={
                    <FormattedMessage
                        id="OperatorSessionReport.sessionOutcome"
                        defaultMessage="Outcome from the session"
                    />
                }
                onChange={({ target: { value } }) => addResolution(value)}
                value={additionalInfo?.resolution || ''}
                inputProps={{ 'data-testid': 'outcome' }}
                select
                fullWidth>
                <MenuItem value="RESOLVED">
                    {getEnumTranslation('sessionOutcome.RESOLVED', {
                        messages: sessionOutcomes,
                    })}
                </MenuItem>
                <MenuItem value="UNRESOLVED">
                    {getEnumTranslation('sessionOutcome.UNRESOLVED', {
                        messages: sessionOutcomes,
                    })}
                </MenuItem>
                <MenuItem value="OPEN">
                    {getEnumTranslation('sessionOutcome.OPEN', {
                        messages: sessionOutcomes,
                    })}
                </MenuItem>
            </TextField>

            <TextField
                label={
                    <FormattedMessage
                        id="OperatorSessionReport.notes"
                        defaultMessage="Session notes"
                    />
                }
                onChange={({ target: { value } }) => addNotes(value)}
                sx={{
                    mt: theme.spacing(4),
                    mb: 0,
                }}
                value={additionalInfo?.text}
                rows={9}
                autoFocus
                multiline
                fullWidth
            />
        </OperatorSessionExtendedPanel>
    );
};
export default OperatorSessionReport;
